import React, { useEffect, useState } from 'react';
import { Box, Card, Grid, Typography, Button, CircularProgress, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PageLayout } from 'components/generics/PageLayout';
import { BarChart, LineChart } from '@mui/x-charts';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerOptionsStore, getServicesIaUsageDailysAnalyticsStore } from 'store/services-ia-usage-dailys/thunks';
import { useResponseHandler } from 'hooks/useResponseHandler';
import moment from 'moment';
import AutocompleteField from 'components/formFields/autocompleteField';
import InputField from 'components/formFields/InputField';
import DatePickerField from 'components/formFields/datePickerField';
import { Formik, Form } from 'formik';
import { formInitialValues, validationSchema, estadisticasDiarioFormModel } from './formik';
import { InfoModal } from 'components/generics/InfoModal';

// Formato de moneda para Tooltip
const valueFormatter = (value: number | null) => `$${value?.toFixed(2) || 0}`;

export const EstadisticasDiarioIa = () => {
    const dispatch: any = useDispatch();
    const { services_ia_usage_dailys_list, customers_for_select } = useSelector((state: any) => state.servicesIaUsageDailys);
    const { handleResponse, ResponseComponents } = useResponseHandler() as any;

    const [loading, setLoading] = useState(false);
    const [openInfoModal, setOpenInfoModal] = useState(false);



     useEffect(() => {
        getFormData();
     }, []);

    const getFormData = async () => {
        const response = await dispatch(getCustomerOptionsStore());
        await handleResponse(response, 'obtener datos del formulario', false);
        
        // Fetch all historical data on page load
        const analyticsResponse = await dispatch(getServicesIaUsageDailysAnalyticsStore({}));
        await handleResponse(analyticsResponse, 'obtener analíticas', false);
    }   

    // Parse string to number
    const data = (services_ia_usage_dailys_list || []).map((item: any) => ({
        date: moment(item.date).format('DD MMM'),
        totalRequests: Number(item.totalRequests) || 0,
        totalTokens: Number(item.totalTokens) || 0,
        totalCredits: Number(item.totalCredits) || 0,
        totalCost: Number(item.totalCost) || 0,
        averageLatency: Number(item.averageLatency) || 0,
    }));

    // Series preparation
    const xLabels = data.map((d: any) => d.date);

    return (
        <PageLayout
            title="Estadísticas de Uso IA (Diario)"
            mainModule="Plataforma IA"
            icon="Insights"
            infoAlert="Consulta las métricas diarias de consumo de inteligencia artificial, tokens utilizados y su costo respectivo."
        >
            {ResponseComponents}

            {/* Controles de filtro */}
            <Card sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0px 4px 8px -4px rgba(105, 108, 255, 0.42)' }}>
                <Formik
                    initialValues={formInitialValues}
                    validationSchema={validationSchema[0]}
                    onSubmit={async (values: any, actions) => {
                        setLoading(true);
                        const response = await dispatch(getServicesIaUsageDailysAnalyticsStore({ 
                            startDate: values.startDate, 
                            endDate: values.endDate, 
                            customerId: values.nombre_cliente?.value 
                        }));
                        await handleResponse(response, 'obtener analíticas', false);
                        setLoading(false);
                        actions.setSubmitting(false);
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 250 }}>
                                    <AutocompleteField
                                        options={customers_for_select}
                                        name={estadisticasDiarioFormModel.formField.nombre_cliente.name}
                                        label={estadisticasDiarioFormModel.formField.nombre_cliente.label}
                                        element={estadisticasDiarioFormModel.formField.nombre_cliente.name}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <DatePickerField
                                        name={estadisticasDiarioFormModel.formField.startDate.name}
                                        label={estadisticasDiarioFormModel.formField.startDate.label}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <DatePickerField
                                        name={estadisticasDiarioFormModel.formField.endDate.name}
                                        label={estadisticasDiarioFormModel.formField.endDate.label}
                                    />
                                </Box>
                                <Button 
                                    type="submit"
                                    variant="contained" 
                                    color="primary" 
                                    disabled={loading || isSubmitting}
                                    sx={{ textTransform: 'none', borderRadius: 2, mt: 0.5 }}
                                >
                                    {loading || isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Filtrar Datos'}
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Card>

            {/* Graficos Principales */}
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Typography variant="h6" fontWeight={600} color="text.secondary">
                                Consumo de Tokens (Diario)
                            </Typography>
                            <IconButton size="small" sx={{ ml: 1 }} color="info" onClick={() => setOpenInfoModal(true)}>
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        {data.length > 0 ? (
                            <LineChart
                                height={300}
                                series={[
                                    { data: data.map((d: any) => d.totalTokens), label: 'Tokens', color: '#696cff' },
                                ]}
                                xAxis={[{ scaleType: 'point', data: xLabels }]}
                                sx={{
                                    '.MuiLineElement-root': { strokeWidth: 3 },
                                    '.MuiMarkElement-root': { strokeWidth: 2, stroke: '#fff', fill: '#696cff' }
                                }}
                            />
                        ) : (
                            <Box height={300} display="flex" alignItems="center" justifyContent="center">
                                <Typography color="text.secondary">No hay datos para el periodo seleccionado</Typography>
                            </Box>
                        )}
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Typography variant="h6" fontWeight={600} color="text.secondary">
                                Costo de Operaciones (USD)
                            </Typography>
                            <IconButton size="small" sx={{ ml: 1 }} color="info" onClick={() => setOpenInfoModal(true)}>
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        {data.length > 0 ? (
                            <BarChart
                                height={300}
                                series={[
                                    { data: data.map((d: any) => d.totalCost), label: 'Costo USD', valueFormatter, color: '#00d27a' },
                                ]}
                                xAxis={[{ scaleType: 'band', data: xLabels }]}
                                sx={{
                                    '.MuiBarElement-root': { rx: 4 } // Bordes redondeados
                                }}
                            />
                        ) : (
                            <Box height={300} display="flex" alignItems="center" justifyContent="center">
                                <Typography color="text.secondary">No hay datos para el periodo seleccionado</Typography>
                            </Box>
                        )}
                    </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Card sx={{ p: 3, borderRadius: 2 }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Typography variant="h6" fontWeight={600} color="text.secondary">
                                Créditos vs Peticiones
                            </Typography>
                            <IconButton size="small" sx={{ ml: 1 }} color="info" onClick={() => setOpenInfoModal(true)}>
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        {data.length > 0 ? (
                            <LineChart
                                height={300}
                                series={[
                                    { data: data.map((d: any) => d.totalCredits), label: 'Créditos Usados', color: '#ffab00' },
                                    { data: data.map((d: any) => d.totalRequests), label: 'Peticiones (API)', color: '#ff3e1d' },
                                ]}
                                xAxis={[{ scaleType: 'point', data: xLabels }]}
                                sx={{
                                    '.MuiLineElement-root': { strokeWidth: 3 },
                                }}
                            />
                        ) : (
                            <Box height={300} display="flex" alignItems="center" justifyContent="center">
                                <Typography color="text.secondary">No hay datos para el periodo seleccionado</Typography>
                            </Box>
                        )}
                    </Card>
                </Grid>
            </Grid>

            <InfoModal
                open={openInfoModal}
                setOpen={setOpenInfoModal}
                title="Interpretación de Estadísticas IA"
                description="Aquí te explicamos de forma sencilla los conceptos clave para entender tus gráficas de consumo de Inteligencia Artificial."
                infoText={
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>Tokens:</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Los modelos de IA (como GPT / GEMINI) no leen palabras, leen "Tokens" (fragmentos de palabras). Representan el volumen total de texto procesado, sumando lo que tú preguntas más lo que la IA responde.
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>Costo USD:</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Es el costo monetario real cobrado por el proveedor de Inteligencia Artificial (por ejemplo OpenAI) por procesar todos los tokens de ese día.
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>Peticiones vs Créditos:</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Las <strong>Peticiones</strong> son el número de interacciones o veces que hablaste con la IA. Los <strong>Créditos</strong> son el saldo interno de tu cuenta que se gastó para cubrir el costo de esas peticiones.
                        </Typography>
                    </Box>
                }
            />
        </PageLayout>
    );
};

